import isFunction from 'lodash/isFunction';
import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import flow from 'lodash/flow';

import OkDialog from 'src/common/components/shared/dialog/OkDialog';
import { selectExperiment } from '../../selectors/config';
import { selectIsSubscriptionEnabled } from '../../selectors/config';
import { selectProducts } from '../../selectors/products';
import { SUBSCRIPTION_PAIRING_PAYMENT_ENABLED } from '../../constants/experiments/subscription';
import { AUTH_PAIRING_FAILURE_DIALOG_MESSAGE } from '../../constants/localizations/auth';
import { showPairingSuccessPageCookie } from '../../constants/cookies';
import { grantDeviceAuth } from '../../actions/auth';
import {
  getUserCodeParam,
  hasPairAuthFlowParam,
  shouldSendToSubscribePair,
} from '../../utils/auth/pairAuthFlowHelpers';
import { setCookie } from '../../utils/cookie';
import { LocationAndLocalizationContext } from '../../providers/LocationAndLocalizationProvider';
import connectWithAuth from './connectWithAuth';

export default function withAuthHandling(handlers = {}, options = {}) {
  const { authRedirectHandler, unauthRedirectHandler } = handlers;
  const { isPartner } = options;

  return (ProtectedComponent) => {
    class WithAuthHandling extends Component {
      static propTypes = {
        isAuthenticated: PropTypes.bool.isRequired,
        isSubscriptionEnabled: PropTypes.bool,
        isSubcribePairingEnabled: PropTypes.bool,
        products: PropTypes.object,
        history: PropTypes.object.isRequired,
        userDetails: PropTypes.object,
        actions: PropTypes.shape({
          grantDeviceAuth: PropTypes.func,
        }),
      };

      static contextType = LocationAndLocalizationContext;

      constructor(props) {
        super(props);

        this.state = {
          showFailureDialog: false,
          failureMessage: '',
        };
      }

      openFailureDialog = (failureMessage) => {
        this.setState({ showFailureDialog: true, failureMessage });
      };

      closeFailureDialog = () => {
        this.setState({ showFailureDialog: false, failureMessage: '' });
      };

      async componentDidUpdate(prevProps) {
        const {
          actions,
          isAuthenticated,
          isSubscriptionEnabled,
          isSubcribePairingEnabled,
          products,
          userDetails,
        } = this.props;
        const { isAuthenticated: wasAuthenticated } = prevProps;
        const { grantDeviceAuth: grantDeviceAuthAction } = actions;
        const { location } = this.context;

        if (isAuthenticated && !wasAuthenticated) {
          const isPairingFlow =
            isPartner && hasPairAuthFlowParam(location.query);
          const sendToSubscribePair = shouldSendToSubscribePair(
            location,
            isSubscriptionEnabled && isSubcribePairingEnabled,
            products,
            userDetails,
          );
          if (isPairingFlow && !sendToSubscribePair) {
            try {
              await grantDeviceAuthAction(getUserCodeParam(location.query));

              setCookie(showPairingSuccessPageCookie.name, 1);
            } catch (error) {
              return this.openFailureDialog(
                AUTH_PAIRING_FAILURE_DIALOG_MESSAGE,
              );
            }
          }

          this.conditionallyReplaceRouteWithDescriptor(
            authRedirectHandler,
            true,
            location,
            {
              userDetails,
              sendToSubscribePair: isPairingFlow && sendToSubscribePair,
            },
          );
        } else if (!isAuthenticated && wasAuthenticated) {
          // Loses authentication
          this.conditionallyReplaceRouteWithDescriptor(
            unauthRedirectHandler,
            false,
            location,
          );
        }
      }

      /**
       *
       * @param {function} handler - Function to get the un/authenticated location descriptor
       * @param {boolean} authFlag - Is the user authenticated?
       * @param {string} location - Location of user
       * @param {object} options - Misc. configurations
       */
      conditionallyReplaceRouteWithDescriptor = (
        handler,
        authFlag,
        location = null,
        handlerOptions = {},
      ) => {
        let locationDescriptor;

        if (isFunction(handler)) {
          locationDescriptor = handler(location, handlerOptions);
          if (locationDescriptor) {
            this.replaceRoute(true, locationDescriptor);
          }
        } else {
          // Refresh current route
          this.replaceRoute(authFlag);
        }
      };

      replaceRoute(authenticated, locationDescriptor) {
        const location = locationDescriptor || window.location;
        this.props.history.replace({
          ...location,
          // NOTE: Needed for the reloadOnPropsChange check in `client/Root.js`
          state: {
            authenticated,
          },
        });
      }

      render() {
        /* eslint-disable no-unused-vars */
        const { failureMessage, showFailureDialog } = this.state;
        const { isAuthenticated, ...otherProps } = this.props;
        const { getLocalizedText } = this.context;

        return (
          <>
            {failureMessage && (
              <OkDialog
                dataTestId="authPageFailureDialog"
                shouldShow={showFailureDialog}
                handleClose={this.closeFailureDialog}
                content={getLocalizedText(failureMessage)}
              />
            )}
            <ProtectedComponent {...otherProps} />;
          </>
        );
      }
    }

    const mapStateToProps = (state) => ({
      products: selectProducts(state),
      isSubscriptionEnabled: selectIsSubscriptionEnabled(state),
      isSubcribePairingEnabled: selectExperiment(
        state,
        SUBSCRIPTION_PAIRING_PAYMENT_ENABLED,
        false,
      ),
    });

    function mapDispatchToProps(dispatch) {
      return {
        actions: bindActionCreators(
          {
            grantDeviceAuth,
          },
          dispatch,
        ),
      };
    }

    return flow(
      connect(mapStateToProps, mapDispatchToProps),
      connectWithAuth,
      withRouter,
    )(WithAuthHandling);
  };
}
